import React, { useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Select, Typography } from "@mui/material";
import useAppInfo from '../../../hooks/appInfo.hook';
import { makeStyles } from '@mui/styles';
import { MbTextField } from '../../../components/MbTextFields';
import MbTextArea from "../../../components/MbTextArea";
import WarningCard from '../../../components/publish/WarningCard';
import ProductPicker from '../../../components/ProductPicker';
import CategoryPicker from '../../../components/CategoryPicker';
import ImageUploaderBlock from '../../../components/ImageUploaderBlock';
import ScheduleDateTimePicker, { MAX_SCHEDULE_DAYS } from './DateTimePicker';
import { useUser } from '../../../contexts/user.context';
import { PLATFORMS } from '../../../utils/constants';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles(() => ({
    label: {
        display: "block",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: 1.8,
        color: "#000"
    }
}))

export default function PushForm({
    isSending,
    onPayloadChange,
    onSend,
    payload,
    showSendButton,
    showScheduling = false,
    sendType = 'now',
    scheduledFor = '',
    onScheduleChange = () => { },
    isEditing = false,
    onEdit
}) {
    const { appInfo, isAppPublished, hasMissingFeature } = useAppInfo();
    const { isBasic, isWebviewTheme } = useUser();
    const navigate = useNavigate();

    const [showPushModal, setShowPushModal] = useState(false);

    const classes = useStyles();

    const scheduledDate = scheduledFor ? new Date(scheduledFor) : null;
    const isScheduleInvalid = showScheduling
        && sendType === 'scheduled'
        && (!scheduledDate
            || scheduledDate.getTime() <= Date.now()
            || scheduledDate.getTime() > Date.now() + MAX_SCHEDULE_DAYS * 24 * 60 * 60 * 1000);

    const isSendDisabled = payload?.text?.length === 0 || isScheduleInvalid;
    const sendButtonLabel = isEditing ? 'salvar' : (showScheduling && sendType === 'scheduled') ? 'agendar' : 'enviar';

    const handleUploadSuccess = (uploadData) => {
        onPayloadChange([{ property: 'image', value: uploadData }]);
    }

    const handleShowPushModalChange = () => {
        onPayloadChange([{ property: 'showPushModal', value: !showPushModal }]);
        setShowPushModal(!showPushModal);
    }

    return (
        <Grid
            container
            component="form"
            spacing={2}
            direction={'row'}
            sx={{ mt: 2 }}
            onSubmit={onSend}
        >
            <Grid item xs={12}>
                <MbTextField
                    helper={`Caracteres restantes: ${60 - payload.title.length}`}
                    label="Título (opcional)"
                    maxLength="60"
                    onChange={(e) => onPayloadChange([{ property: 'title', value: e.target.value }])}
                    value={payload.title}
                />
            </Grid>

            <Grid item xs={12}>
                <MbTextArea
                    helper={`Caracteres restantes: ${400 - payload.text.length}`}
                    label="Mensagem"
                    maxLength="400"
                    onChange={(e) => onPayloadChange([{ property: 'text', value: e.target.value }])}
                    rows="4"
                    value={payload.text}
                />
            </Grid>

            <Grid item xs={12}>
                <ImageUploaderBlock
                    label='Imagem (opcional)'
                    onDeleteSuccess={() => onPayloadChange([{ property: 'image', value: {} }])}
                    onUploadSuccess={handleUploadSuccess}
                    helperText={<>Tamanho máximo: 100 KB<br />Resolução recomendada: 300 x 300 px</>}
                    maxFileSizeInKB={100}
                    imageUrl={payload?.image?.url}
                />
            </Grid>

            <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth>
                    <label className={classes.label}>Ação</label>
                    <Select
                        fullWidth
                        value={payload.action}
                        onChange={(e) => onPayloadChange([{ property: 'action', value: e.target.value }])}
                        displayEmpty
                    >
                        <MenuItem value={'noAction'}>Abrir o aplicativo</MenuItem>
                        {!isBasic && !isWebviewTheme && (
                            <MenuItem value={'goToProduct'}>Abrir o aplicativo na tela de um produto</MenuItem>
                        )}
                        {!isBasic && !isWebviewTheme && (
                            <MenuItem value={'goToCategory'}>Abrir o aplicativo em uma categoria de produtos</MenuItem>
                        )}
                        {!isBasic && !isWebviewTheme && !hasMissingFeature('search_term_action') && (
                            <MenuItem value={'goToSearch'}>Abrir o aplicativo em uma pesquisa de produtos</MenuItem>
                        )}
                        <MenuItem value={'goToUrl'}>Abrir uma URL no navegador do celular</MenuItem>
                        {!hasMissingFeature('go_to_webview_action_on_push') && (
                            <MenuItem value={'goToWebview'}>Abrir uma URL dentro do aplicativo</MenuItem>
                        )}
                    </Select>
                    <Typography variant="caption">Selecione a ação que irá acontecer ao clicar na notificação</Typography>
                </FormControl>
            </Grid>

            {payload.action === 'goToProduct' &&
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <ProductPicker
                            selectedProductId={payload.link || ''}
                            onPicked={(p) => {
                                const dataArray = [{ property: 'link', value: String(p.id) }, { property: 'linkName', value: String(p.name) }];

                                if (p.url && appInfo.platformId === PLATFORMS.IRROBA) {
                                    dataArray.push({ property: 'productUrl', value: String(p.url) })
                                }

                                onPayloadChange(dataArray)
                            }}
                        />
                        <Typography variant="caption">
                            Selecione o produto que será exibido ao clicar na notificação
                        </Typography>
                    </FormControl>
                </Grid>
            }

            {payload.action === 'goToCategory' &&
                <Grid item xs={12}>
                    <FormControl fullWidth className="mt-16 mb-16">
                        <CategoryPicker
                            selectedCategoryId={Number(payload.link) || ''}
                            onPicked={(c) => onPayloadChange([{ property: 'link', value: String(c.id) }, { property: 'linkName', value: String(c.name) }])}
                        />
                        <Typography variant="caption">Selecione a categoria que será exibida ao clicar na notificação</Typography>
                    </FormControl>
                </Grid>
            }

            {payload.action === 'goToSearch' &&
                <Grid item xs={12}>

                    <FormControl fullWidth>
                        <MbTextField
                            label="Termo de pesquisa"
                            value={payload.link || ''}
                            onChange={(e) => onPayloadChange([{ property: 'link', value: String(e.target.value) }])}
                        />
                        <Typography variant="caption">
                            Insira o termo de pesquisa que será usado para buscar os produtos na sua loja
                        </Typography>
                    </FormControl>
                </Grid>
            }

            {payload.action === 'goToUrl' &&
                <Grid item xs={12}>

                    <FormControl fullWidth>
                        <MbTextField
                            label="URL"
                            value={payload.link || ''}
                            onChange={(e) => onPayloadChange([{ property: 'link', value: String(e.target.value) }])}
                        />
                        <Typography variant="caption">
                            Insira a URL que será aberta no navegador do celular ao
                            clicar na notificação. Ex.: https://www.minhaloja.com.br/promocao
                        </Typography>
                    </FormControl>
                </Grid>
            }

            {payload.action === 'goToWebview' &&
                <Grid item xs={12}>

                    <FormControl fullWidth>
                        <MbTextField
                            label="URL"
                            value={payload.link || ''}
                            onChange={(e) => onPayloadChange([{ property: 'link', value: String(e.target.value) }])}
                        />
                        <Typography variant="caption">
                            Insira a URL que será aberta no aplicativo ao
                            clicar na notificação. Ex.: https://www.minhaloja.com.br/promocao
                        </Typography>
                    </FormControl>
                </Grid>
            }

            <Grid item xs={12}>
                <FormControl fullWidth>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showPushModal || payload.showPushModal}
                                name="showPushModal"
                                onChange={() => handleShowPushModalChange()}
                            />
                        }
                        label="Exibir notificação push dentro do aplicativo"
                    />
                    <Typography variant="caption">
                        Marcando esta opção, ao clicar na notificação, o seu conteúdo
                        será exibido dentro de uma janela assim que o aplicativo for aberto
                    </Typography>
                </FormControl>
            </Grid>

            {showScheduling &&
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <label className={classes.label}>Envio</label>
                        <RadioGroup
                            row
                            value={sendType}
                            onChange={(e) => onScheduleChange({ sendType: e.target.value })}
                        >
                            <FormControlLabel value="now" control={<Radio />} label="Enviar agora" />
                            <FormControlLabel value="scheduled" control={<Radio />} label="Agendar" />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            }

            {showScheduling && sendType === 'scheduled' &&
                <Grid item xs={12}>
                    <ScheduleDateTimePicker
                        value={scheduledFor}
                        onChange={(value) => onScheduleChange({ scheduledFor: value })}
                    />
                </Grid>
            }

            {showSendButton &&
                <Grid item xs={12}>
                    {isAppPublished() ? (
                        <Box display='flex' justifyContent='center' marginY={2} gap={2}>
                            {isSending ? <CircularProgress /> :
                                <>
                                    <Button variant="outlined" onClick={() => navigate('/push')}>
                                        Voltar
                                    </Button>

                                    <Button
                                        disabled={isSendDisabled}
                                        onClick={() => { isEditing ? onEdit() : onSend() }}
                                        variant="contained"
                                        style={{ textTransform: 'uppercase' }}
                                    >
                                        {sendButtonLabel}
                                    </Button>
                                </>
                            }
                        </Box>
                    ) : (
                        <WarningCard content="Seu aplicativo deve estar disponível para 
                        download para que seja possível enviar uma notificação push."
                        />
                    )}
                </Grid>
            }
        </Grid>
    )
}
